import React from 'react';
// template components
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';

export default () => (
  <SectionContainer
    id='life-insurance-faq-apply-policy-loans'
    backgroundVariant='lightB'
    themeType='lightTheme'>
    <Row align='center'>
      <Col medium={8}>
        <Typography
          component='h2'
          nextElement='typography'
          align='center'>
          Apply for a Policy Loan
        </Typography>
        <Typography nextElement='typography'>
          One of the most valuable features of a life insurance policy is the ability to borrow from your
          policy&#39;s cash value. However, taking out a policy loan can have a significant impact on your policy&#39;s performance and future non&#45;guaranteed annual dividends. Also, any outstanding loan balance and interest will reduce proceeds paid to your beneficiary.
        </Typography>
        <Typography nextElement='typography'>
          Talk to your Northwestern Mutual representative before making any decisions.
        </Typography>
        <Typography nextElement='typography'>
          If you have a policy that is dated after Dec. 31, 1981, or that has been amended to direct
          recognition, a loan may reduce the annual non&#45;guaranteed dividends you receive on your policy.
          Direct Recognition is the process Northwestern Mutual uses to determine the dividends that are paid
          on a whole life policy. It factors in the amount of loan activity on the policy in order to fairly
          distribute the company&#39;s surplus to all policyowners.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Applying for a Loan
        </Typography>
        <Typography nextElement='typography'>
          To apply for a policy loan of <strong>more than &#36;100,000</strong>, you&#39;ll need to complete
          a Policy Loan Agreement form. To obtain a copy of the form, ask your representative or call
          Northwestern Mutual at{' '}
          <Link
            id='life-insurance-faq-apply-policy-loan-link'
            ariaLabel='Call to apply for a policy loan'
            linkUrl='tel:8003888123'
            variant='primaryDeemphasize'>
            1&#45;800&#45;388&#45;8123
          </Link>
          ,{' '}
          <time dateTime='7:00-06:00'>
            7am
          </time>
          &#8211;
          <time dateTime='18:00-06:00'>
            6pm
          </time>
          {' '}CT, Monday &#8211; Friday.
        </Typography>
        <Typography nextElement='typography'>
          Please have your policy number ready when you call.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          How Interest Affects Your Policy
        </Typography>
        <Typography nextElement='typography'>
          Interest on your loan accrues daily and is billed annually until the loan is repaid. Unpaid
          interest is added to principal where it continues to accrue and compound. This may result in the
          loan exceeding the cash value of your policy. If this happens, you will need to pay back some or all
          of the loan to avoid termination and a potentially significant taxable gain.
        </Typography>
        <Typography nextElement='typography'>
          How Loans Affect Certain Policy Types
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Variable Life and Variable CompLife policies
        </Typography>
        <Typography nextElement='typography'>
          Taking a loan from a Variable Life or a Variable CompLife will permanently affect your
          policy, even if you repay the loan in full. That&#39;s because the cash value used as collateral for
          your loan will be placed in the company&#39;s general account and will not share in the investment
          experience of the underlying funds.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Policies Classified as Modified Endowment Contracts
        </Typography>
        <Typography nextElement='typography'>
          Loans and loan interest are taxable when taken as ordinary income to the extent of the gain
          in the policy. Northwestern Mutual is required by the Internal Revenue Service &#40;IRS&#41; to report either the amount you borrow or the gain in the contract, whichever is less. If you are under age 59 1&#47;2 when you take the loan, the IRS may assess an additional 10&#37; penalty on the taxable amount.Modified Endowment Contracts may be subject to other special tax treatment. Talk with your tax consultant before making your decision.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Repaying Policy Loans
        </Typography>
        <Typography nextElement='typography'>
          Repaying your loan is important. It keeps your policy in force and maintains the level of
          protection you planned to provide for your beneficiaries. Repayment can restore your policy&#39;s death benefit and cash value, and may increase non&#45;guaranteed annual dividend performance. As you pay off your loan, you will also reduce your interest payments&#8212;a lower balance means lower annual interest charges.
        </Typography>
        <Typography nextElement='typography'>
          To repay a policy loan:
        </Typography>
        <BulletedList
          bulletedListData={[
            { bulletText: 'Contact your representative' },
            {
              bulletText: <>
                Call Northwestern Mutual at{' '}
                <Link
                  id='life-insurance-apply-policy-loans-toll-free-number-link'
                  ariaLabel='Call to repay a policy loan'
                  linkUrl='tel:8003888123'
                  variant='primaryDeemphasize'>
                  1&#45;800&#45;388&#45;8123
                </Link>
                ,{' '}
                <time dateTime='7:00-06:00'>
                  7am
                </time>
                &#8211;
                <time dateTime='18:00-06:00'>
                  6pm
                </time>
                {' '}CT, Monday &#8211; Friday
              </>,
            },
            {
              bulletText: <>
                Sign&#45;up or Log in to your Billing Account to make one&#45;time payments on outstanding loan balances.
              </>,
            },
            {
              bulletText: (<>
                <strong>Send payment to:</strong>
                <BulletedList
                  bulletedListData={[
                    { bulletText: 'Northwestern Mutual' },
                    { bulletText: <>Milwaukee, WI 53288&#45;0245</> },
                    { bulletText: 'PO Box 88245' },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <Typography nextElement='typography'>
          Please write your policy number&#40;s&#41; on your payment to ensure proper crediting.
        </Typography>
        <Typography nextElement='typography'>
          You can repay your loan in installments, in a lump sum, or with your scheduled bills.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Installments
        </Typography>
        <Typography nextElement='typography'>
          You choose the amount and the frequency of the payment.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Lump&#45;sum payment
        </Typography>
        <Typography nextElement='typography'>
          You repay the loan all at once.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Scheduled bill payments
        </Typography>
        <Typography disableBottomPadding>
          If you pay your premium through an Insurance Service Account, we can add your loan payments
          to your bill monthly, quarterly or every six months. Payments can be as little as &#36;10 a month.
        </Typography>
      </Col>
    </Row>
  </SectionContainer>
);
